import type { ClassTemplateUpdateInput } from "@/types/class_template"
import { useSchoolSettings } from "@/hooks/api/useSchoolSettings"
import { memo, useEffect, useId, useMemo } from "react"

import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import type { Resolver } from "react-hook-form"

import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Portal,
  Switch,
  Textarea,
} from "@chakra-ui/react"

import { Field } from "@/components/atoms/Field"

const skipLinkStyle = {
  position: "absolute",
  left: "-9999px",
  top: "-9999px",
  background: "#3182ce",
  color: "white",
  padding: "8px 16px",
  textDecoration: "none",
  borderRadius: "6px",
  zIndex: 1000,
} as const

type ClassTemplateFormValues = ClassTemplateUpdateInput

interface ClassTemplateFormDialogProps {
  isOpen: boolean
  title: string
  submitLabel: string
  resolver: Resolver<ClassTemplateFormValues>
  defaultValues: ClassTemplateFormValues
  onSubmit: (values: ClassTemplateFormValues) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  cancelLabel?: string
  finalFocusRef?: React.RefObject<HTMLElement | null>
  showSkipLink?: boolean
  loadingText?: string
}

const ClassTemplateFormDialog = memo(
  ({
    isOpen,
    title,
    submitLabel,
    resolver,
    defaultValues,
    onSubmit,
    onClose,
    isSubmitting = false,
    cancelLabel = "Cancel",
    finalFocusRef,
    showSkipLink = false,
    loadingText,
  }: ClassTemplateFormDialogProps) => {
    const formSectionId = useId()
    const { programTypes, swimLevels, locations } = useSchoolSettings()

    const normalizedDefaults = useMemo<ClassTemplateFormValues>(
      () => ({
        name: defaultValues.name ?? "",
        slug: defaultValues.slug ?? "",
        description: defaultValues.description ?? null,
        program_type: defaultValues.program_type ?? undefined,
        instructor_id: defaultValues.instructor_id ?? null,
        swim_level: defaultValues.swim_level ?? undefined,
        location: defaultValues.location ?? undefined,
        day_of_week: defaultValues.day_of_week ?? undefined,
        start_time: defaultValues.start_time ?? "",
        end_time: defaultValues.end_time ?? null,
        duration_minutes: defaultValues.duration_minutes ?? null,
        max_students: defaultValues.max_students ?? undefined,
        min_students: defaultValues.min_students ?? null,
        current_enrollment: defaultValues.current_enrollment ?? null,
        waitlist_enabled: defaultValues.waitlist_enabled ?? false,
        price: defaultValues.price ?? undefined,
        prerequisites: defaultValues.prerequisites ?? null,
        notes: defaultValues.notes ?? null,
        is_active: defaultValues.is_active ?? false,
        is_published: defaultValues.is_published ?? false,
      }),
      [defaultValues],
    )

    const form = useForm<ClassTemplateFormValues>({
      resolver,
      mode: "onBlur",
      defaultValues: normalizedDefaults,
    })

    useEffect(() => {
      if (isOpen) {
        void form.reset(normalizedDefaults)
      }
    }, [form, isOpen, normalizedDefaults])

    const handleClose = () => {
      form.reset(normalizedDefaults)
      onClose()
    }

    const handleSubmit: SubmitHandler<ClassTemplateFormValues> = async (
      values,
    ) => {
      try {
        await onSubmit(values)
        handleClose()
      } catch (error) {
        // Error handling is delegated to the mutation layer
      }
    }

    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            handleClose()
          }
        }}
        size={{ base: "full", md: "xl" }}
        trapFocus
        restoreFocus
        initialFocusEl={() => null}
        finalFocusEl={() => finalFocusRef?.current ?? null}
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner
            inset="0"
            display="grid"
            placeItems="center"
            padding={{ base: 4, md: 6 }}
          >
            <DialogContent as="form" onSubmit={form.handleSubmit(handleSubmit)} maxH="90vh" display="flex" flexDirection="column">
              {showSkipLink && (
                <a
                  href={`#${formSectionId}`}
                  style={skipLinkStyle}
                  onFocus={(event) => {
                    event.currentTarget.style.left = "16px"
                    event.currentTarget.style.top = "16px"
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.left = "-9999px"
                    event.currentTarget.style.top = "-9999px"
                  }}
                >
                  Skip to form fields
                </a>
              )}

              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody id={formSectionId} overflowY="auto" flex="1">
                <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
                  <Field
                    required
                    invalid={!!form.formState.errors.name}
                    errorText={form.formState.errors.name?.message}
                    label="Name"
                  >
                    <Input
                      placeholder={"Enter name"}
                      aria-invalid={!!form.formState.errors.name}
                      {...form.register("name")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.slug}
                    errorText={form.formState.errors.slug?.message}
                    label="Slug"
                  >
                    <Input
                      placeholder={"Enter slug"}
                      aria-invalid={!!form.formState.errors.slug}
                      {...form.register("slug")}
                    />
                  </Field>

                  <Box gridColumn={{ md: "1 / -1" }}>
                    <Field
                      invalid={!!form.formState.errors.description}
                      errorText={form.formState.errors.description?.message}
                      label="Description"
                    >
                      <Textarea
                        {...form.register("description")}
                        placeholder={"Enter description"}
                        rows={3}
                        resize="vertical"
                        aria-invalid={!!form.formState.errors.description}
                      />
                    </Field>
                  </Box>

                  <Field
                    required
                    invalid={!!form.formState.errors.program_type}
                    errorText={form.formState.errors.program_type?.message}
                    label="Program Type"
                  >
                    <select
                      {...form.register("program_type")}
                      aria-invalid={!!form.formState.errors.program_type}
                      className="chakra-select"
                    >
                      {programTypes.map((pt) => (
                        <option key={pt.value} value={pt.value}>{pt.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.instructor_id}
                    errorText={form.formState.errors.instructor_id?.message}
                    label="Instructor Id"
                  >
                    <Input
                      placeholder={"Enter instructor id"}
                      aria-invalid={!!form.formState.errors.instructor_id}
                      {...form.register("instructor_id")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.swim_level}
                    errorText={form.formState.errors.swim_level?.message}
                    label="Swim Level"
                  >
                    <select
                      {...form.register("swim_level")}
                      aria-invalid={!!form.formState.errors.swim_level}
                      className="chakra-select"
                    >
                      {swimLevels.map((sl) => (
                        <option key={sl.value} value={sl.value}>{sl.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.location}
                    errorText={form.formState.errors.location?.message}
                    label="Location"
                  >
                    <select
                      {...form.register("location")}
                      aria-invalid={!!form.formState.errors.location}
                      className="chakra-select"
                    >
                      {locations.map((loc) => (
                        <option key={loc.value} value={loc.value}>{loc.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.day_of_week}
                    errorText={form.formState.errors.day_of_week?.message}
                    label="Day Of Week"
                  >
                    <select
                      {...form.register("day_of_week")}
                      aria-invalid={!!form.formState.errors.day_of_week}
                      className="chakra-select"
                    >
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Sunday">Sunday</option>
                    </select>
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.start_time}
                    errorText={form.formState.errors.start_time?.message}
                    label="Start Time"
                  >
                    <Input
                      placeholder={"Enter start time"}
                      aria-invalid={!!form.formState.errors.start_time}
                      {...form.register("start_time")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.end_time}
                    errorText={form.formState.errors.end_time?.message}
                    label="End Time"
                  >
                    <Input
                      placeholder={"Enter end time"}
                      aria-invalid={!!form.formState.errors.end_time}
                      {...form.register("end_time")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.duration_minutes}
                    errorText={form.formState.errors.duration_minutes?.message}
                    label="Duration Minutes"
                  >
                    <Input
                      type="number"
                      step={1}
                      placeholder={"Enter duration minutes"}
                      aria-invalid={!!form.formState.errors.duration_minutes}
                      {...form.register("duration_minutes", {
                        valueAsNumber: true,
                      })}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.max_students}
                    errorText={form.formState.errors.max_students?.message}
                    label="Max Students"
                  >
                    <Input
                      type="number"
                      step={1}
                      placeholder={"Enter max students"}
                      aria-invalid={!!form.formState.errors.max_students}
                      {...form.register("max_students", {
                        valueAsNumber: true,
                      })}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.min_students}
                    errorText={form.formState.errors.min_students?.message}
                    label="Min Students"
                  >
                    <Input
                      type="number"
                      step={1}
                      placeholder={"Enter min students"}
                      aria-invalid={!!form.formState.errors.min_students}
                      {...form.register("min_students", {
                        valueAsNumber: true,
                      })}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.current_enrollment}
                    errorText={
                      form.formState.errors.current_enrollment?.message
                    }
                    label="Current Enrollment"
                  >
                    <Input
                      type="number"
                      step={1}
                      placeholder={"Enter current enrollment"}
                      aria-invalid={!!form.formState.errors.current_enrollment}
                      {...form.register("current_enrollment", {
                        valueAsNumber: true,
                      })}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.waitlist_enabled}
                    errorText={form.formState.errors.waitlist_enabled?.message}
                    label="Waitlist Enabled"
                  >
                    <Controller
                      name="waitlist_enabled"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="waitlist_enabled"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Waitlist Enabled"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.price}
                    errorText={form.formState.errors.price?.message}
                    label="Price per Class"
                    helperText="Amount charged for each individual class"
                  >
                    <Input
                      type="number"
                      step={0.01}
                      placeholder={"Enter price per class (e.g., 45.00)"}
                      aria-invalid={!!form.formState.errors.price}
                      {...form.register("price", { valueAsNumber: true })}
                    />
                  </Field>

                  <Box gridColumn={{ md: "1 / -1" }}>
                    <Field
                      invalid={!!form.formState.errors.prerequisites}
                      errorText={form.formState.errors.prerequisites?.message}
                      label="Prerequisites"
                    >
                      <Textarea
                        {...form.register("prerequisites")}
                        placeholder={"Enter prerequisites"}
                        rows={3}
                        resize="vertical"
                        aria-invalid={!!form.formState.errors.prerequisites}
                      />
                    </Field>
                  </Box>

                  <Box gridColumn={{ md: "1 / -1" }}>
                    <Field
                      invalid={!!form.formState.errors.notes}
                      errorText={form.formState.errors.notes?.message}
                      label="Notes"
                    >
                      <Textarea
                        {...form.register("notes")}
                        placeholder={"Enter notes"}
                        rows={3}
                        resize="vertical"
                        aria-invalid={!!form.formState.errors.notes}
                      />
                    </Field>
                  </Box>

                  <Field
                    invalid={!!form.formState.errors.is_active}
                    errorText={form.formState.errors.is_active?.message}
                    label="Is Active"
                  >
                    <Controller
                      name="is_active"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="is_active"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Is Active"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.is_published}
                    errorText={form.formState.errors.is_published?.message}
                    label="Is Published"
                  >
                    <Controller
                      name="is_published"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="is_published"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Is Published"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>
                </Box>
              </DialogBody>

              <DialogFooter>
                <HStack gap={3}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="submit"
                    colorPalette="blue"
                    loading={isSubmitting}
                    loadingText={loadingText ?? `${submitLabel}...`}
                  >
                    {submitLabel}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

ClassTemplateFormDialog.displayName = "ClassTemplateFormDialog"

export default ClassTemplateFormDialog
